import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';
import type { TypeProductMenuCode } from '~/constants/product';

import { productKeys } from './keys';

import type {
  TypeProductCodeName,
  TypeProductEnumCode,
  TypeProductMappingPageCodesMenu,
  TypeProductOperatingPageCodesMenu,
  TypeProductRegisterPageCodesMenu,
} from '~/types/product.types';

/**
 * [상품관리] 공통 코드 조회 API
 * @param menuCode 메뉴 코드
 */
export const fetchCodesMenuMenuCode = async <T>(menuCode: TypeProductMenuCode): Promise<T> => {
  const { data } = await axiosInstance.get<{ data: T }>(`${API_ENDPOINT.PRODUCT.GET_PRODUCT_CODES_MENU}/${menuCode}`);
  return data.data;
};

type UseGetCodesMenuMenuCodeQueryOptions<TData, TSelected> = {
  menuCode: TypeProductMenuCode;
  enabled?: boolean;
  showGlobalDimOverlay?: boolean;
  select?: (data: TData) => TSelected;
};

/**
 * [상품관리] 공통 코드 조회 Query Hook
 * @description 상품 필터 옵션, 상태코드 등 공통코드 조회
 */
export const useGetCodesMenuMenuCodeQuery = <TData = unknown, TSelected = TData>({
  menuCode,
  enabled = true,
  showGlobalDimOverlay = true,
  select,
}: UseGetCodesMenuMenuCodeQueryOptions<TData, TSelected>) => {
  return useQuery({
    queryKey: productKeys.codesMenu(menuCode),
    queryFn: () => fetchCodesMenuMenuCode<TData>(menuCode),
    staleTime: 1000 * 60 * 30,
    enabled,
    meta: { showGlobalDimOverlay },
    select,
  });
};

const mockEnumCode = (code: string, name: string, codeItemList: TypeProductCodeName[]): TypeProductEnumCode => ({
  code,
  name,
  description: null,
  codeItemList,
});

const YES_NO_ITEMS: TypeProductCodeName[] = [
  { code: 'Y', name: '예' },
  { code: 'N', name: '아니오' },
];

const SKU_KEYWORD = mockEnumCode('skuKeyword', '키워드', [
  { code: 'SKU_CODE', name: '상품코드' },
  { code: 'SKU_NAME', name: '상품명' },
  { code: 'SUPPLIER_NAME', name: '공급사명' },
]);

const PRODUCT_USAGE_STATUS = mockEnumCode('productUsageStatus', '상품 사용여부', [
  { code: 'Y', name: '사용중' },
  { code: 'N', name: '사용중단' },
]);

/** 상품 등록/수정·운영 상품 조회 모달이 함께 읽는 코드. 모달은 키가 하나라도 빠지면 옵션 변환에서 멈춘다. */
const PRODUCT_MODAL_CODES = {
  combinedPacking: mockEnumCode('combinedPacking', '상온냉장 합포 여부', YES_NO_ITEMS),
  productUsageStatus: PRODUCT_USAGE_STATUS,
  shelfLifeType: mockEnumCode('shelfLifeType', '기한정책', [
    { code: 'EXPIRATION', name: '유통기한' },
    { code: 'MANUFACTURE', name: '제조일자' },
  ]),
  keepLevel: mockEnumCode('keepLevel', '보관유형', [
    { code: 'ROOM', name: '상온' },
    { code: 'COLD', name: '냉장' },
    { code: 'FROZEN', name: '냉동' },
  ]),
  oneDayProduct: mockEnumCode('oneDayProduct', '일판매여부', YES_NO_ITEMS),
  sellingType: mockEnumCode('sellingType', '판매방법', [{ code: 'NORMAL', name: '일반' }]),
  fragile: mockEnumCode('fragile', '유리포함', YES_NO_ITEMS),
  wettable: mockEnumCode('wettable', '눌림주의', YES_NO_ITEMS),
  needRefrigerant: mockEnumCode('needRefrigerant', '컬리냉매제', YES_NO_ITEMS),
  unit: mockEnumCode('unit', '단위', [{ code: 'EA', name: 'EA' }]),
  productOperationType: mockEnumCode('productOperationType', '상품 운영 종류', [{ code: 'NORMAL', name: '일반' }]),
  cosmeticPacking: mockEnumCode('cosmeticPacking', '뷰티포장', YES_NO_ITEMS),
  deliveryType: mockEnumCode('deliveryType', '샛별', [{ code: 'DAWN', name: '샛별' }]),
  deliveryDocumentNum: mockEnumCode('deliveryDocumentNum', '택배', [{ code: 'PARCEL', name: '택배' }]),
  massDocumentNum: mockEnumCode('massDocumentNum', '대량', [{ code: 'MASS', name: '대량' }]),
  kurlyKeepType: mockEnumCode('kurlyKeepType', '컬리 보관 유형', [{ code: 'COLD', name: '냉장' }]),
  inspection: mockEnumCode('inspection', '검품 대상 여부', YES_NO_ITEMS),
  deliveryDispatchType: mockEnumCode('deliveryDispatchType', '배송 배차 유형', [{ code: 'NORMAL', name: '일반' }]),
  packingDirection: mockEnumCode('packingDirection', '정방향여부', YES_NO_ITEMS),
};

const skuRegCodesMenuResponse: { data: TypeProductRegisterPageCodesMenu } = {
  data: {
    ...PRODUCT_MODAL_CODES,
    skuActiveType: mockEnumCode('skuActiveType', '상품 운영 구분', [
      { code: 'true', name: '운영 상품' },
      { code: 'false', name: '미운영 상품' },
    ]),
    skuStatusType: mockEnumCode('skuStatusType', '상품 등록 상태', [
      { code: 'ALL', name: '전체' },
      { code: 'REQUEST', name: '등록 요청' },
      { code: 'WAIT', name: '등록 대기' },
      { code: 'COMPLETE', name: '등록 완료' },
    ]),
    skuKeyword: SKU_KEYWORD,
  },
};

const prodSkuCodesMenuResponse: { data: TypeProductOperatingPageCodesMenu } = {
  data: {
    ...PRODUCT_MODAL_CODES,
    skuKeyword: SKU_KEYWORD,
    skuStatusActiveType: mockEnumCode('skuStatusActiveType', '상품 등록 상태', [{ code: 'ACTIVE', name: '등록 완료' }]),
    skuActiveTrueType: mockEnumCode('skuActiveTrueType', '상품 운영 구분', [{ code: 'true', name: '운영 상품' }]),
  },
};

const skuMappingCodesMenuResponse: { data: TypeProductMappingPageCodesMenu } = {
  data: {
    skuActiveType: mockEnumCode('skuActiveType', '상품 운영 구분', [
      { code: 'true', name: '운영 상품' },
      { code: 'false', name: '미운영 상품' },
    ]),
    klsSalesKeyword: mockEnumCode('klsSalesKeyword', '키워드', [
      { code: 'SKU_CODE', name: '상품코드' },
      { code: 'SKU_NAME', name: '상품명' },
      { code: 'SALES_CHANNEL_NAME', name: '판매처명' },
    ]),
    skuKeyword: SKU_KEYWORD,
  },
};

// 메뉴 코드가 경로 파라미터라 mock 응답 함수로 구분할 수 없어, 메뉴별 경로로 따로 등록한다.
registerMockAPI({
  method: 'get',
  endpoint: `${API_ENDPOINT.PRODUCT.GET_PRODUCT_CODES_MENU}/SKU_REG`,
  label: '상품/상품등록수정/공통코드',
  scenarios: [{ name: '정상 응답', response: skuRegCodesMenuResponse }],
});

registerMockAPI({
  method: 'get',
  endpoint: `${API_ENDPOINT.PRODUCT.GET_PRODUCT_CODES_MENU}/PROD_SKU`,
  label: ['상품/운영상품조회/공통코드', '반품교환/교환신청/교환상품추가'],
  scenarios: [{ name: '정상 응답', response: prodSkuCodesMenuResponse }],
});

registerMockAPI({
  method: 'get',
  endpoint: `${API_ENDPOINT.PRODUCT.GET_PRODUCT_CODES_MENU}/SKU_MAPPING`,
  label: '상품/상품판매처매핑/공통코드',
  scenarios: [{ name: '정상 응답', response: skuMappingCodesMenuResponse }],
});
